import IconBackward from 'assets/icons/audio-backward.svg';
import IconForward from 'assets/icons/audio-forward.svg';
import IconNext from 'assets/icons/audio-next.svg';
import IconPause from 'assets/icons/audio-pause.svg';
import IconPlay from 'assets/icons/audio-play.svg';
import IconPrevious from 'assets/icons/audio-previous.svg';
import { DivWithBackgroundImage } from 'components';
import { useAudioStats } from 'modules/audio-stats';
import { SeekBar } from 'modules/audios/components/SeekBar';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAudioPlayer } from '../hooks';
import {
  clearPlaybackPosition,
  readPlaybackPosition,
  storePlaybackPosition,
} from '../utils';

interface Props {
  playerIsExpanded: boolean;
  isPlaylist?: boolean;
  audio: AudioInfo;
  favoriteButton: JSX.Element | null;
  playlistButton: JSX.Element | null;
  expandButton: JSX.Element;
}

type Speed = 1 | 1.25 | 1.5 | 1.75 | 2.0;

const playbackRates: Speed[] = [1, 1.25, 1.5, 1.75, 2.0];

/** How often playback progress is mirrored to local storage while audio is
 * playing. Local writes are synchronous and free; Firestore writes are neither,
 * and each one comes back through the audio-stats listener as a billed read, so
 * they happen only at the boundaries below — never on a timer. */
const LOCAL_SYNC_INTERVAL = 2000;

/** Initialise audio element to control with component. */
let PLAYER = new Audio();

export const AudioPlayerCore: React.FC<Props> = ({
  audio,
  isPlaylist,
  ...props
}) => {
  const [speed, setSpeed] = useState<Speed>(playbackRates[0]);
  /** State updater so we can get the correct player state (current time, duration) at all times. */
  const [refresher, setRefresher] = useState<number>(0);
  const { stats, onTrackCompleted, updatePlayProgress } = useAudioStats();
  const lastLocalSync = useRef(0);
  const resumeApplied = useRef(false);
  /** Progress mirrored locally but not yet published. Carries its own track, so
   * it stays publishable after `audio` has already been swapped out. */
  const pendingProgress = useRef<
    { track: AudioInfo; percent: number; currentTime: number } | undefined
  >(undefined);
  const {
    showNext,
    showPrevious,
    playing,
    next,
    previous,
    play,
    pause,
    close,
  } = useAudioPlayer();

  const player = useMemo(() => {
    /** Unset source to stop loading previous value */
    PLAYER.src = '';
    /** Load new audio URL */
    PLAYER.src = audio.audioUrl;
    PLAYER.onplay = play;
    PLAYER.onpause = pause;
    PLAYER.onended = onEnd;
    PLAYER.ontimeupdate = onTimeUpdate;
    PLAYER.title = `${audio.title} by ${audio.author}`;
    PLAYER.autoplay = true;
    /** The starting position is restored by the resume effect below, once the
     * metadata is loaded and the audio stats have arrived. */
    lastLocalSync.current = 0;
    resumeApplied.current = false;

    return PLAYER;
  }, [audio]);

  const updateProgressCallback = useCallback(
    (currentTime: number, duration: number, syncRemote = true) => {
      /** Duration is NaN until the metadata loads, which would otherwise persist
       * a NaN percentage and a position the player never actually reached. */
      if (!isFinite(duration) || duration <= 0) return;

      const percent = (currentTime / duration) * 100;

      if (percent >= 99) {
        /** Completion is only recorded by a remote flush. A local-only tick
         * this close to the end must keep mirroring rather than clear, or a
         * kill in the last seconds resumes from the previous boundary. */
        if (!syncRemote) {
          storePlaybackPosition({
            trackId: audio.id,
            secondsListened: currentTime,
          });
          pendingProgress.current = { track: audio, percent, currentTime };
          return;
        }

        clearPlaybackPosition(audio.id);
        pendingProgress.current = undefined;
        onTrackCompleted(audio);
        return;
      }

      storePlaybackPosition({
        trackId: audio.id,
        secondsListened: currentTime,
      });

      if (!syncRemote) {
        pendingProgress.current = { track: audio, percent, currentTime };
        return;
      }

      pendingProgress.current = undefined;
      updatePlayProgress(audio, percent, currentTime);
    },
    [audio],
  );

  /** Starting another track, or loading a playlist, swaps `audio` without
   * passing through the boundary handlers — `loadTracks` calls `play()` while
   * playback is already running, so the `[playing]` flush never fires either.
   * The local mirror holds one track, so the outgoing position is overwritten
   * within LOCAL_SYNC_INTERVAL. Publish it before that happens. */
  useEffect(() => {
    return () => {
      const pending = pendingProgress.current;
      if (!pending) return;

      pendingProgress.current = undefined;
      updatePlayProgress(pending.track, pending.percent, pending.currentTime);
    };
  }, [audio]);

  /** Media element events keep firing while the tab is backgrounded, unlike the
   * refresher interval below, which Android throttles to roughly once a minute.
   * This is therefore the only thing keeping progress current during background
   * playback — which is when the app is most likely to be killed outright.
   * Local only: the mirror is what a kill is recovered from, and anything not
   * yet published is flushed by the boundary handlers or, when a track is
   * swapped out from under the player, by the effect above. */
  function onTimeUpdate() {
    const now = Date.now();
    if (now - lastLocalSync.current < LOCAL_SYNC_INTERVAL) return;

    lastLocalSync.current = now;
    updateProgressCallback(PLAYER.currentTime, PLAYER.duration, false);
  }

  async function onEnd() {
    clearPlaybackPosition(audio.id);
    /** Cleared before `next()` swaps the track, or the flush above would
     * republish the last tick over the completion reset. */
    pendingProgress.current = undefined;
    onTrackCompleted(audio);
    next();
  }

  function handlePlay() {
    player.play();
  }

  function handlePause() {
    player.pause();
  }

  function handleClose() {
    updateProgressCallback(player.currentTime, player.duration);
    close();
    player.pause();
    player.src = '';
  }

  function handleNext() {
    updateProgressCallback(player.currentTime, player.duration);
    next();
  }

  function handlePrevious() {
    updateProgressCallback(player.currentTime, player.duration);
    previous();
  }

  /** Scrubbing and skipping are local-only. They are repeatable in a way the
   * boundary handlers are not — one tap per write with no rate limit — and the
   * user has not left the track, so there is nothing to publish yet. */
  function seekTo(position: number) {
    if (!player) return;
    player.currentTime = position;
    /** The thumb is dragged natively; without a render the fill stays where it
     * was until the refresher next fires, up to half a second behind. */
    setRefresher(+new Date());
    updateProgressCallback(position, player.duration, false);
  }

  function skipForwards() {
    if (!player) return;

    if (player.duration - player.currentTime > 15) {
      player.currentTime += 15;
    } else {
      player.currentTime = player.duration;
    }
    updateProgressCallback(player.currentTime, player.duration, false);
  }

  function skipBackwards() {
    if (!player) return;

    if (player.currentTime > 15) {
      player.currentTime -= 15;
    } else {
      player.currentTime = 0;
    }

    updateProgressCallback(player.currentTime, player.duration, false);
  }

  /** Refresh state every second so current time updates.  */
  useEffect(() => {
    const interval = setInterval(() => setRefresher(+new Date()), 500);
    return () => clearInterval(interval);
  }, []);

  /** Update playback rate when speed changes */
  useEffect(() => {
    if (!player) return;
    player.playbackRate = speed;
  }, [speed, player, audio]);

  useEffect(() => {
    updateProgressCallback(player.currentTime, player.duration);
  }, [playing]);

  /** Restore the last known position. The local mirror is the freshest source
   * since it survives a background kill, with the audio stats as the
   * cross-device fallback. Stats load asynchronously, so this reruns when they
   * arrive, but only while the track is still at its start — a late listener
   * must not drag a listener back to where they were yesterday. */
  useEffect(() => {
    if (resumeApplied.current || player.currentTime > 1) return;

    const stored = readPlaybackPosition(audio.id);
    const position =
      stored?.secondsListened ??
      stats.find(stat => stat.trackId === audio.id)?.secondsListened;

    /** Nothing known yet — the stats listener may still be in flight. */
    if (position === undefined) return;

    resumeApplied.current = true;
    if (position <= 0) return;

    if (player.readyState > 0) {
      player.currentTime = position;
      return;
    }

    /** Seeking is only possible once the duration is known. */
    player.addEventListener(
      'loadedmetadata',
      () => {
        player.currentTime = position;
      },
      { once: true },
    );
  }, [audio, stats, player]);

  /** Android kills backgrounded PWAs without warning. Hiding and unloading are
   * the last moments at which the position can still be written. */
  useEffect(() => {
    function flush() {
      updateProgressCallback(player.currentTime, player.duration);
    }

    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') flush();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', flush);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', flush);
    };
  }, [player, updateProgressCallback]);

  /** Navigator controls metadata load */
  useEffect(() => {
    navigator.mediaSession.metadata = new MediaMetadata({
      artist: audio.author,
      title: audio.title,
      album: '',
      artwork: [
        {
          src:
            audio.thumbnailUrl ||
            require('assets/images/placeholder-square.png'),
          sizes: '80x80',
          type: 'image/*',
        },
      ],
    });
  }, [audio]);

  /** Play / pause controls */
  useEffect(() => {
    navigator.mediaSession.setActionHandler('play', handlePlay);
    navigator.mediaSession.setActionHandler('pause', handlePause);
  }, [handlePlay, handlePause]);

  useEffect(() => {
    if (!player) return;
    const { duration, currentTime, playbackRate } = player;

    if (!duration || duration < currentTime) return;

    navigator.mediaSession.setPositionState({
      // Duration must always be positive and larger than the current position
      duration: duration,
      // Position can never be a negative value, so fallback to 0 if value is lower than 0
      position: currentTime,
      playbackRate: playbackRate,
    });
    /** @param refresher must be a dependency because that's triggering the state update, the @param player reference does not refresh the component. Once the updater does it's thing, you can read the actual value from @param player */
  }, [refresher]);

  /** Previous / skip backwards controls */
  useEffect(() => {
    navigator.mediaSession.setActionHandler(
      'previoustrack',
      showPrevious ? handlePrevious : null,
    );

    navigator.mediaSession.setActionHandler(
      'seekbackward',
      showPrevious ? null : skipBackwards,
    );
  }, [showPrevious, handlePrevious, skipBackwards]);

  /** Next / skip forwards controls */
  useEffect(() => {
    navigator.mediaSession.setActionHandler(
      'nexttrack',
      showNext ? handleNext : null,
    );

    navigator.mediaSession.setActionHandler(
      'seekforward',
      showNext ? null : skipForwards,
    );
  }, [showNext, handleNext, skipForwards]);

  const renderSeekBar = () => {
    if (!player) return;

    return (
      <SeekBar
        position={player.currentTime}
        duration={player.duration}
        onSlide={seekTo}
      />
    );
  };

  const renderPlaybackRates = () => (
    <>
      <div className="player__naration f f--gap-small">
        {playbackRates.map(rate => {
          return (
            <button
              onClick={() => setSpeed(rate)}
              key={rate}
              className={`button button--unpadded button--ghost player__naration-speed ${
                speed === rate
                  ? 'player__naration-speed--active'
                  : 'player__naration-speed--inactive'
              }`}
            >
              {rate.toFixed(2)}x
            </button>
          );
        })}
      </div>
      <p className="player__naration-label u-opacity--60">Narration speed</p>
    </>
  );

  const renderControls = () => {
    return (
      <React.Fragment>
        <div className="player__controls">
          <button
            disabled={!showPrevious}
            className="button button--unpadded"
            onClick={handlePrevious}
          >
            <img
              className=""
              style={{ opacity: showPrevious ? 1 : 0.3 }}
              src={IconPrevious}
              alt="Previous"
            />
          </button>
          <button className="button button--unpadded" onClick={skipBackwards}>
            <img className="" src={IconBackward} alt="Skip back" />
          </button>
          {player?.paused ? (
            <button className="button button--unpadded" onClick={handlePlay}>
              <img className="" src={IconPlay} alt="Play button" />
            </button>
          ) : (
            <button className="button button--unpadded" onClick={handlePause}>
              <img className="" src={IconPause} alt="Pause button" />
            </button>
          )}
          <button className="button button--unpadded" onClick={skipForwards}>
            <img className="" src={IconForward} alt="Skip forward" />
          </button>
          <button
            disabled={!showNext}
            className="button button--unpadded"
            onClick={handleNext}
          >
            <img
              className=""
              style={{ opacity: showNext ? 1 : 0.3 }}
              src={IconNext}
              alt="Next"
            />
          </button>
        </div>
        {renderPlaybackRates()}
      </React.Fragment>
    );
  };

  const {
    favoriteButton,
    expandButton,
    playlistButton,
    playerIsExpanded,
  } = props;

  return (
    <>
      <div className="persistent-player">
        <div
          className={`${
            playerIsExpanded
              ? 'persistent-player__player persistent-player__player--expanded'
              : 'persistent-player__player'
          }`}
        >
          {renderSeekBar()}
          <div className="player">
            <div className="player__info">
              <DivWithBackgroundImage
                fallbackImageUrl={require('assets/images/placeholder-member.jpg')}
                mainImageUrl={audio.thumbnailUrl}
                classes="player__visual"
              />
              <div className="player__copy">
                <h3 className="player__title t-bold">{audio && audio.title}</h3>
                <p className="player__author t-bold u-opacity--40">
                  by {audio ? audio.author : 'Unknown'}
                </p>
              </div>
            </div>
            <div className="player__all-controls">{renderControls()}</div>
            <div className="player__actions">
              {favoriteButton}
              {playlistButton}
              <button
                className="button button--ghost button--unpadded"
                onClick={handleClose}
              >
                <svg width="16" height="17">
                  <g
                    transform="translate(0 .4161)"
                    fill="none"
                    fillRule="evenodd"
                  >
                    <circle fill="#31263A" opacity=".2" cx="8" cy="8" r="8" />
                    <g
                      stroke="#FFF"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M11.5 4.5l-7 7" />
                      <path d="M4.5 4.5l7 7" />
                    </g>
                  </g>
                </svg>
              </button>
            </div>
          </div>
          {expandButton}
        </div>
      </div>
    </>
  );
};
